import { Timeline, Tooltip } from "../../utils/local-antd";
import type { TimelineItemProps } from "../../utils/local-antd";
import { Flex } from "../Flex";
import capitalize from "lodash/capitalize";
import styled from "styled-components";
import { Rounded } from "../../theme/Rounded";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons/faCircleUser";
import { AtiraIcon } from "../AtiraIcon";
import { Text } from "../Text";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { standardDate } from "../../utils/Date";
import { AtiraEmpty } from "../AtiraEmpty";
import { BaseActivity } from "../../model/activity/BaseActivity";
import { EntryActivity } from "../../model/activity/EntryActivity";
import { TaskActivity } from "../../model/activity/TaskActivity";
import { KanbanCardActivity } from "../../model/activity/KanbanCardActivity";
import { ActivityTimelineCollapse } from "./ActivityTimelineCollapse";
import { Spacing } from "../../theme/Spacing";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { ThemeType } from "../../theme/Theme";
import {
  getActivityDotColor,
  getActivityHeadline,
  getActivityIcon,
  hasActivityDetails,
} from "./utils/activityUtils";
import { ImageCreateEdit } from "../ImageCreateEdit";
import { UserProfileWrapper } from "../UserProfileWrapper";
import { permissionSliceSelectors } from "../../redux/permission/permission.selector";

const TimeLineWrapper = styled(Flex)`
  .ant-timeline {
    width: 100%;
  }
`;

const CardWrapper = styled(Flex)`
  flex-direction: column;
  background-color: ${({ theme }) => theme.lightSub};
  border-radius: ${Rounded.lg};
  padding: ${Spacing.m};
  gap: ${Spacing.s};
  transition: transform 0.2s ease;
  width: 100%;
`;

const DotWrapper = styled(Flex)<{ color: keyof ThemeType }>`
  background-color: ${({ color, theme }) => theme[color]};
  width: 1.5rem;
  height: 1.5rem;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
`;

type Props<
  T extends BaseActivity | EntryActivity | TaskActivity | KanbanCardActivity,
> = {
  activities: T[];
};

export const ActivityTimeline = <
  T extends BaseActivity | EntryActivity | TaskActivity | KanbanCardActivity,
>({
  activities,
}: Props<T>) => {
  const { t } = useTranslation();
  const user = useAppSelector(userSliceSelectors?.selectLoggedInUser);
  const members = useAppSelector(permissionSliceSelectors.selectMembers);

  const items: TimelineItemProps[] =
    activities?.map((activity) => {
      // A blank `userId` is not a lookup that failed - it is what a public,
      // unauthenticated form submission records, since there is no logged-in
      // account to credit. Telling the two apart from a bare "User" is the
      // difference between "a website visitor did this" and "someone did this
      // and we can't say who".
      const activityUser = activity?.user ||
        [...members, user].find(
          (member) => member?._id === activity.userId,
        ) ||
        (activity.userId
          ? { name: t("common.user"), picture: "" }
          : { name: t("common.publicSubmission"), picture: "" });

      return {
        key: activity._id,
        dot: (
          <DotWrapper color={getActivityDotColor(activity.action)}>
            <AtiraIcon
              icon={getActivityIcon(activity.action)}
              size="sm"
              color="white"
            />
          </DotWrapper>
        ),
        children: (
          <CardWrapper>
            <Flex alignItems="center" gap="m">
              <Tooltip title={capitalize(activityUser.name)}>
                <UserProfileWrapper>
                  {activityUser.picture ? (
                    <ImageCreateEdit src={activityUser.picture} />
                  ) : (
                    <AtiraIcon icon={faCircleUser} size="2x" color="main" />
                  )}
                </UserProfileWrapper>
              </Tooltip>

              <Text fontWeight="bold" fontSize="xs">
                {activityUser.name}
              </Text>
            </Flex>

            <Text>{getActivityHeadline(activity, t)}</Text>

            {hasActivityDetails(activity) ? (
              <ActivityTimelineCollapse activity={activity} />
            ) : null}

            <Flex
              alignItems="center"
              gap="s"
              justifyContent="flex-end"
              marginTop="m"
            >
              <Text color="darkerSub" fontSize="s">
                {standardDate<string>(activity.createdAt, user, true)}
              </Text>

              <AtiraIcon icon={faCalendar} color="darkerSub" size="sm" />
            </Flex>
          </CardWrapper>
        ),
      };
    }) || [];

  if (!items?.length) {
    return <AtiraEmpty description={t("activity.actions.drawer.empty")} />;
  }

  return (
    <TimeLineWrapper>
      <Timeline items={items} />
    </TimeLineWrapper>
  );
};
